import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Wallet, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import PoweredBy from "@/components/PoweredBy";
import PayPalModal from "@/components/PayPalModal";

const FEATURES = [
  "13 in-depth chapters with mid-chapter checkpoints",
  "Live 1040 practice returns",
  "AI Tax Teacher (Claude Sonnet 5) inside every chapter",
  "Downloadable study guides + certificate",
  "Ask a human instructor",
  "Highlighter, bookmarks & streak tracker",
];

const FEATURES_1120 = [
  "13 in-depth chapters covering every line of Form 1120",
  "Practice returns filled out on the ACTUAL form line numbers",
  "Schedules L, M-1, M-2 walkthroughs",
  "§179, bonus depreciation, DRD, PHC & AET traps",
  "20-question final exam + printable certificate",
  "Free preview: Chapters 1–3 and 3 practice returns",
];

const FEATURES_BUNDLE = [
  "EVERYTHING from Master the 1040 (13 chapters)",
  "EVERYTHING from Mastering Form 1120 (13 chapters)",
  "17 realistic practice returns (4 individual + 13 corporate)",
  "2 final exams · 2 printable certificates",
  "AI Tax Teacher + Ask a human instructor",
  "Save $150 vs. buying separately — INTRODUCTORY PRICE",
];

const FEATURES_AFSP = [
  "IRS Circular 230 study guide — full PDF download",
  "18-question AFTR-style practice exam with instant scoring",
  "Rationale after every question — learn while you review",
  "Domain coverage: New Tax Law · General Review · Ethics",
  "Retake the quiz unlimited times",
  "Built for seasoned preparers — no 1040 course fluff",
];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const { branding } = useBranding();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [cashTag, setCashTag] = useState("");
  const [paypalHandle, setPaypalHandle] = useState("");
  const [cashPlan, setCashPlan] = useState(null);
  const [paypalPlan, setPaypalPlan] = useState(null);
  const [cashNote, setCashNote] = useState("");
  const [cashSending, setCashSending] = useState(false);
  const [trialStarting, setTrialStarting] = useState(false);

  useEffect(() => {
    api.get("/payments/plans").then(r => {
      setPlans(r.data.plans);
      setCashTag(r.data.cash_app_tag || "");
      setPaypalHandle(r.data.paypal_handle || "");
    });
  }, []);

  const openPayPal = (plan) => {
    if (!user) { nav("/register"); return; }
    setPaypalPlan(plan);
  };

  const openCash = (plan) => {
    if (!user) { nav("/register"); return; }
    setCashPlan(plan);
  };

  const sendCashApp = async () => {
    if (!user) { nav("/register"); return; }
    setCashSending(true);
    try {
      await api.post("/payments/cash-app-request", {
        plan: cashPlan.id,
        cashtag_note: cashNote,
      });
      toast.success("Payment logged. An instructor will verify and unlock your access.");
      setCashPlan(null); setCashNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setCashSending(false); }
  };

  const cashLink = (plan) => `https://cash.app/${(cashTag || "").replace(/^\$/, "$")}/${plan.amount}`;
  const copyTag = async () => {
    try {
      await navigator.clipboard.writeText(cashTag);
      toast.success("Cashtag copied");
    } catch (e) {
      toast.error("Copy failed — select the Cashtag and copy manually");
    }
  };

  const startTrial = async () => {
    if (!user) { nav("/register"); return; }
    setTrialStarting(true);
    try {
      await api.post("/trial/start-instructor");
      const fresh = await refresh();
      toast.success(`Instructor trial started — you have ${fresh?.trial_days_left || 2} days.`);
      nav("/admin");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial");
    } finally { setTrialStarting(false); }
  };

  const trialAvailable = user && user.role !== "admin" && !user.trial_expires_at && user.plan !== "instructor";

  return (
    <div className="min-h-screen bg-background paper-texture">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="pricing-brand-link">
            {branding.logo_url ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" /> :
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{branding.brand_name?.[0]}</div>}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <div className="flex gap-3 text-sm items-center">
            <Link to="/afsp-bundle" className="hidden sm:inline underline text-accent hover:text-accent/80 font-medium" data-testid="pricing-nav-afsp">AFSP Prep</Link>
            {user ? (
              <Button variant="ghost" onClick={() => nav(user.role === "admin" ? "/admin" : "/dashboard")} data-testid="pricing-nav-dashboard">Back to app</Button>
            ) : (
              <>
                <Link to="/login" className="underline" data-testid="pricing-nav-login">Sign in</Link>
                <Link to="/register" className="underline" data-testid="pricing-nav-register">Create account</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 lg:p-12">
        <div className="text-center mb-12">
          <div className="label-caps mb-3">Enrollment</div>
          <h1 className="font-serif text-4xl md:text-6xl leading-none mb-4">Pick your plan.</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            One payment. Lifetime access to the {branding.brand_name} curriculum, exam and certificate.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-2">
            <Sparkles size={14} className="text-primary" />
            <span className="text-sm">Not sure yet?</span>
            <Link
              to="/preview"
              className="text-sm font-medium text-primary underline hover:text-primary/80"
              data-testid="pricing-cta-preview"
            >
              Try a sample lesson &amp; quiz →
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {plans.map(p => (
            <Card
              key={p.id}
              className={`p-6 flex flex-col relative ${p.id === "bundle" ? "border-2 border-primary shadow-lg" : ""} ${p.id === "afsp_prep" ? "border-2 border-accent" : ""}`}
              data-testid={`plan-card-${p.id}`}
            >
              {p.id === "bundle" && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-semibold tracking-widest uppercase px-3 py-1 rounded-full whitespace-nowrap">
                  Best Value · Save $150
                </div>
              )}
              {p.id === "afsp_prep" && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-[10px] font-semibold tracking-widest uppercase px-3 py-1 rounded-full whitespace-nowrap">
                  AFTR Exam Prep
                </div>
              )}
              <div className="flex items-baseline justify-between mb-2">
                <div className="label-caps text-[10px]">
                  {p.id === "instructor" ? "For teachers"
                    : p.id === "corporate" ? "Corporate"
                    : p.id === "bundle" ? "Both courses"
                    : p.id === "afsp_prep" ? "AFTR exam prep"
                    : p.id === "whitelabel" ? "For businesses"
                    : "For learners"}
                </div>
                <div className="text-right">
                  {p.id === "bundle" && p.regular_amount && (
                    <div className="text-xs line-through text-muted-foreground">${p.regular_amount.toFixed(0)}</div>
                  )}
                  <div className="font-serif text-4xl">${p.amount.toFixed(0)}</div>
                  {p.id === "bundle" && p.introductory && (
                    <div className="text-[10px] uppercase tracking-widest text-primary mt-1">Introductory</div>
                  )}
                </div>
              </div>
              <h2 className="font-serif text-2xl mb-3">{p.name}</h2>
              <p className="text-xs text-muted-foreground mb-5">{p.description}</p>
              <ul className="space-y-2 mb-6 flex-1">
                {(p.id === "corporate" ? FEATURES_1120
                  : p.id === "bundle" ? FEATURES_BUNDLE
                  : p.id === "afsp_prep" ? FEATURES_AFSP
                  : FEATURES).map(f => (
                  <li key={f} className="flex items-start gap-2 text-xs">
                    <Check size={12} className="mt-1 shrink-0 text-primary" />
                    <span>{f}</span>
                  </li>
                ))}
                {p.id === "instructor" && (
                  <>
                    <li className="flex items-start gap-2 text-xs">
                      <Check size={12} className="mt-1 shrink-0 text-accent" />
                      <span>Add unlimited students & track their progress</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs">
                      <Check size={12} className="mt-1 shrink-0 text-accent" />
                      <span>White-label the app with your logo & colors</span>
                    </li>
                  </>
                )}
              </ul>
              <div className="space-y-2">
                {p.id === "corporate" && (
                  <Button
                    asChild
                    variant="outline"
                    className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    data-testid={`preview-1120-btn`}
                  >
                    <Link to="/course/1120">
                      <Sparkles size={14} className="mr-2" /> See free preview first
                    </Link>
                  </Button>
                )}
                {p.id === "afsp_prep" && (
                  <Button
                    asChild
                    variant="outline"
                    className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    data-testid="afsp-details-btn"
                  >
                    <Link to="/afsp-bundle">
                      <Sparkles size={14} className="mr-2" /> See what's inside
                    </Link>
                  </Button>
                )}
                {p.id === "instructor" && trialAvailable && (
                  <Button
                    variant="outline"
                    className="w-full border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                    onClick={startTrial}
                    disabled={trialStarting}
                    data-testid="start-instructor-trial-btn"
                  >
                    <Sparkles size={14} className="mr-2" />
                    {trialStarting ? "Starting…" : "Try 2 days free"}
                  </Button>
                )}
                <Button
                  className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white"
                  onClick={() => openPayPal(p)}
                  data-testid={`paypal-open-${p.id}`}
                >
                  <Wallet size={14} className="mr-2" />
                  Pay ${p.amount.toFixed(0)} with PayPal
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => openCash(p)}
                  data-testid={`cashapp-open-${p.id}`}
                >
                  <Wallet size={14} className="mr-2" />
                  Pay with Cash App
                </Button>
              </div>
            </Card>
          ))}
        </div>

        <PoweredBy className="mt-12 text-center" />
      </div>

      {cashPlan && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={() => setCashPlan(null)}>
          <div className="bg-card border border-border rounded-md max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="cashapp-modal">
            <div className="label-caps mb-2">Cash App · {cashPlan.name}</div>
            <h3 className="font-serif text-3xl mb-4">Pay ${cashPlan.amount.toFixed(0)} to {cashTag}</h3>            <div className="flex items-center justify-center mb-5" data-testid="cashapp-qr">
              <div className="bg-white p-3 rounded-md border border-border">
                <QRCodeSVG
                  value={cashLink(cashPlan)}
                  size={168}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="M"
                  imageSettings={{
                    src: "https://cash.app/favicon.ico",
                    height: 28,
                    width: 28,
                    excavate: true,
                  }}
                />
              </div>
            </div>
            <div className="text-center text-xs text-muted-foreground mb-4">
              📱 <strong>On your phone?</strong> Scan the QR — it opens Cash App with the amount pre-filled to {cashTag}.
            </div>
            <ol className="space-y-3 text-sm mb-5">
              <li><strong>1.</strong> Open Cash App and send <strong>${cashPlan.amount.toFixed(0)}</strong> to <strong>{cashTag}</strong>.</li>
              <li><strong>2.</strong> Include your email (<em>{user?.email || "the one you'll register with"}</em>) in the note so we can match the payment.</li>
              <li><strong>3.</strong> Tap the button below — an instructor will verify and unlock your access.</li>
            </ol>
            <div className="flex gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={copyTag} data-testid="copy-cashtag-btn"><Copy size={12} className="mr-1" /> Copy Cashtag</Button>
              <a href={cashLink(cashPlan)} target="_blank" rel="noreferrer" className="inline-flex" data-testid="cashapp-external-link">
                <Button variant="outline" size="sm"><Wallet size={12} className="mr-1" /> Open Cash App</Button>
              </a>
            </div>
            <Input
              placeholder="Optional note (e.g., 'Sent from $YourTag @ 3:12 PM')"
              value={cashNote}
              onChange={e => setCashNote(e.target.value)}
              className="mb-3"
              data-testid="cashapp-note-input"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setCashPlan(null)} data-testid="cashapp-cancel-btn">Cancel</Button>
              <Button onClick={sendCashApp} disabled={cashSending} data-testid="cashapp-submit-btn">
                {cashSending ? "Submitting…" : "I've paid — verify me"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <PayPalModal
        open={!!paypalPlan}
        onClose={() => setPaypalPlan(null)}
        plan={paypalPlan}
        paypalHandle={paypalHandle}
        user={user}
      />
    </div>
  );
}
